// src/modules/app-views/omnichannel/pages/Init/components/ConversationHeader.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Avatar, Button, Flex, Select, Tag, Tooltip } from 'antd';
import { UserOutlined, WhatsAppOutlined, SyncOutlined, DisconnectOutlined } from '@ant-design/icons';
import { BiExpandAlt, BiCollapseAlt } from 'react-icons/bi';
import { GrGroup } from 'react-icons/gr';
import { VscArchive } from 'react-icons/vsc';
import TranslateLanguageSelect, {
  TRANSLATE_LANGUAGES,
  renderFlag,
} from './TranslateLanguageSelect';
import { getCountryMetadata } from 'utils/phone';
import { parsePhoneToDisplay } from 'utils/helpers';
import translateX from 'utils/translateX';
import WhatsAppSourceTag from './WhatsAppSourceTag';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const WA_GREEN = '#25D366'; // verify-ignore - WhatsApp brand color
// Em-dash sentinel used by parent to indicate no display name
const EMPTY_DISPLAY_NAME = '\u2014'; // verify-ignore

const AVATAR_REFRESH_OVERLAY_BASE = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'opacity 0.2s',
  fontSize: 16,
  pointerEvents: 'none',
};

// *******************************************************************************
// COMPONENT
// *******************************************************************************

const ConversationHeader = ({
  conversation,
  displayName,
  sources,
  selectedSourceId,
  sourceSelectOpen,
  onSourceChange,
  onSourceSelect,
  onSourceOpenChange,
  translateLang,
  onTranslateLangChange,
  translating,
  translatedLangs,
  isFullscreen,
  onToggleFullscreen,
  isDark,
  token,
  isInQueue,
  isChannelRemoved,
  MASKED_PHONE,
  onRefreshAvatar,
  refreshingAvatar,
  embedded,
}) => {
  // 5. LOCAL VARIABLES II
  const headerStyle = {
    boxShadow: isDark ? '0 1px 3px rgba(0,0,0,0.4)' : '0 1px 3px rgba(11,20,26,0.13)',
    flexWrap: 'wrap',
    height: 'auto',
    minHeight: 68,
    gap: 8,
    paddingTop: 8,
    paddingBottom: 8,
  };

  const avatarOverlayStyle = {
    ...AVATAR_REFRESH_OVERLAY_BASE,
    borderRadius: conversation?.isGroup ? 4 : '50%',
    background: 'rgba(0,0,0,0.35)',
    opacity: refreshingAvatar ? 1 : 0,
    color: token.colorWhite,
  };

  const avatarStyle = { opacity: refreshingAvatar ? 0.5 : 1, border: `1px solid ${token.colorBorder}` };

  const channelRemovedStyle = {
    height: 40,
    minWidth: 180,
    padding: '0 10px',
    borderRadius: 6,
    border: `1px solid ${token.colorErrorBorder}`,
    background: token.colorErrorBg,
  };

  // 9. UI
  return (
    <div
      className={`chat-content-header${embedded ? ' is-embedded' : ''}`}
      style={headerStyle}
    >
      <style>{`
        .chat-header-name   { order: 1; }
        .chat-source-select { order: 2; min-width: 220px; }
        .chat-header-menu   { order: 3; }
        @media (max-width: 768px) {
          .chat-header-name   { order: 1; }
          .chat-header-menu   { order: 2; }
          .chat-source-select {
            order: 3;
            width: 100% !important;
            min-width: 0 !important;
            flex-basis: 100% !important;
          }
        }

        /* Embedded (LeftDrawer) - always two lines */
        /* Line 1: photo / name / phone   Line 2: translate + source */
        .chat-content-header.is-embedded .chat-header-name {
          flex: 0 0 100% !important;
        }
        .chat-content-header.is-embedded .chat-source-select {
          flex: 0 0 100% !important;
          width: 100% !important;
          min-width: 0 !important;
          margin-left: 0 !important;
        }

        @media (min-width: 992px) {
          .chat-content-header.is-embedded .chat-source-select {
            justify-content: center;
          }
        }

        @media (max-width: 991px) {
          .chat-content-header.is-embedded .chat-header-name {
            justify-content: center;
          }
          .chat-content-header.is-embedded .chat-source-select {
            justify-content: center;
          }
          .chat-content-header.is-embedded .chat-source-select > *:last-child {
            flex: 1 !important;
            min-width: 0 !important;
          }
        }

        .avatar-refresh-wrap:hover .avatar-refresh-overlay { opacity: 1 !important; }
      `}</style>

      {/* Avatar + name */}
      <Flex align="center" gap={8} className="chat-header-name" style={{ flex: 1, minWidth: 0 }}>
        <div
          className="avatar-refresh-wrap"
          style={{ position: 'relative', display: 'inline-flex', cursor: onRefreshAvatar ? 'pointer' : 'default' }}
          onClick={onRefreshAvatar}
          title={onRefreshAvatar ? 'Atualizar foto' : undefined}
        >
          <Avatar
            src={conversation?.contactSnapshot?.avatar || undefined}
            size={40}
            shape={conversation?.isGroup ? 'square' : 'circle'}
            style={avatarStyle}
            icon={
              conversation?.isGroup && !conversation?.contactSnapshot?.avatar
                ? <GrGroup />
                : !conversation?.isGroup && !conversation?.contactSnapshot?.avatar && !/[A-Za-z0-9]/.test(((displayName !== EMPTY_DISPLAY_NAME ? displayName : conversation?.contactSnapshot?.phone || '?').charAt(0)))
                  ? <UserOutlined />
                  : undefined
            }
          >
            {conversation?.isGroup ? null : (() => {
              const ch = (displayName !== EMPTY_DISPLAY_NAME ? displayName : conversation?.contactSnapshot?.phone || '?').charAt(0).toUpperCase();
              return /[A-Za-z0-9]/.test(ch) ? ch : null;
            })()}
          </Avatar>
          {onRefreshAvatar && (
            <div className="avatar-refresh-overlay" style={avatarOverlayStyle}>
              <SyncOutlined spin={refreshingAvatar} />
            </div>
          )}
        </div>
        <Flex vertical gap={2}>
          <h5 className="mb-0" style={{ color: token.colorText, lineHeight: 1.2, display: 'flex', alignItems: 'center', gap: 6 }}>
            {displayName}
            {conversation?.isGroup && (
              <Tooltip title={translateX('group')}><GrGroup style={{ fontSize: 14 }} /></Tooltip>
            )}
            {conversation?.archived && (
              <Tooltip title={translateX('archived')}><VscArchive style={{ fontSize: 14 }} /></Tooltip>
            )}
            {conversation?.channel === 'whatsapp' && (
              <WhatsAppOutlined style={{ color: WA_GREEN }} />
            )}
          </h5>
          {conversation?.isGroup ? (
            (() => {
              const members = conversation?.contactSnapshot?.members || [];
              const owner = members.find(m => m.role === 'owner' || m.role === 'superadmin')
                || members.find(m => m.role === 'admin');
              let label = '';
              if (owner) {
                const ownerName = owner.name || '';
                const ownerPhone = owner.phone ? parsePhoneToDisplay(ensurePlus(owner.phone), undefined, { showCountryCode: true, masked: MASKED_PHONE }) : '';
                label = ownerName
                  ? `${translateX('created_by')} ${ownerName} ${ownerPhone}`
                  : ownerPhone ? `${translateX('created_by')} ${ownerPhone}` : '';
              }
              if (!label && members.length > 0) {
                label = `${members.length} ${translateX('members')}`;
              }
              if (!label) return null;
              return <span style={{ fontSize: 12, color: token.colorTextSecondary, lineHeight: 1.2 }}>{label}</span>;
            })()
          ) : (
            conversation?.contactSnapshot?.phone && (
              <span style={{ fontSize: 12, color: token.colorTextSecondary, lineHeight: 1.2 }}>
                {parsePhoneToDisplay(ensurePlus(conversation.contactSnapshot.phone), undefined, { showCountryCode: true, masked: MASKED_PHONE })}
              </span>
            )
          )}
          {translatedLangs?.length > 0 && (
            <Tooltip title={translateX('conversation_already_translated')}>
              <Flex gap={2} style={{ marginTop: 1, lineHeight: 1 }}>
                {translatedLangs.map(lang => {
                  const entry = TRANSLATE_LANGUAGES.find(l => l.lang === lang);
                  if (!entry) return null;
                  const meta = getCountryMetadata(entry.country);
                  return (
                    <span key={lang} style={{ fontSize: 10, lineHeight: '12px', cursor: 'default' }}>
                      {renderFlag(meta, entry.country, { fontSize: 12, lineHeight: '14px' })}
                    </span>
                  );
                })}
              </Flex>
            </Tooltip>
          )}
        </Flex>
      </Flex>

      {/* Language selector + Source selector */}
      <Flex className="chat-source-select" align="center" gap={4}>
        <TranslateLanguageSelect
          value={translateLang}
          onChange={onTranslateLangChange}
          disabled={isInQueue || translating || isChannelRemoved}
          style={{ height: 40 }}
        />

        {isChannelRemoved ? (
          <Flex align="center" gap={6} style={channelRemovedStyle}>
            <DisconnectOutlined style={{ color: token.colorError, fontSize: 14 }} />
            <Flex vertical style={{ lineHeight: 1.3 }}>
              <span style={{ fontSize: 11, color: token.colorError, fontWeight: 600 }}>
                {translateX('channel_unavailable')}
              </span>
              <span style={{ fontSize: 10, color: token.colorErrorBorder }}>
                {conversation?.sourcePhoneNumber
                  ? formatSourcePhone(conversation.sourcePhoneNumber)
                  : translateX('channel_removed')}
              </span>
            </Flex>
          </Flex>
        ) : sources.length > 0 && (
          <Select
            value={selectedSourceId}
            onChange={onSourceChange}
            disabled={isInQueue}
            open={sourceSelectOpen}
            onSelect={onSourceSelect}
            onOpenChange={onSourceOpenChange}
            style={{ height: 40, minWidth: 220 }}
            placeholder="Selecionar fonte"
            popupMatchSelectWidth={false}
            options={sources.map(s => ({
              value: s._id,
              label: (
                <Flex align="center" gap={6} className="pr-2">
                  <Avatar src={s.profilePicture || undefined} size={24} style={{ flexShrink: 0 }}>
                    {(s.displayName || 'W').charAt(0).toUpperCase()}
                  </Avatar>
                  <Flex vertical style={{ lineHeight: 1.3 }}>
                    <span style={{ fontSize: 12, fontWeight: 500 }}>
                      {s.displayName || 'WhatsApp'}
                    </span>
                    <span style={{ fontSize: 10, color: token.colorTextTertiary }}>
                      {formatSourcePhone(s.phoneNumber) || '-'}
                    </span>
                  </Flex>
                  <WhatsAppSourceTag sourceType={s.sourceType} size="small" style={{ marginLeft: 'auto' }} />
                </Flex>
              ),
            }))}
          />
        )}
      </Flex>

      {/* Menu buttons */}
      <Flex className="chat-header-menu" align="center" gap={4}>
        {onToggleFullscreen && (
          <Tooltip title={isFullscreen ? 'Restaurar janela' : 'Exibir tela toda'}>
            <Button
              type="default"
              icon={
                isFullscreen ? (
                  <BiCollapseAlt style={{ fontSize: 18, display: 'flex' }} />
                ) : (
                  <BiExpandAlt style={{ fontSize: 18, display: 'flex' }} />
                )
              }
              onClick={onToggleFullscreen}
            />
          </Tooltip>
        )}
      </Flex>
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ConversationHeader;

// *******************************************************************************
// HELPERS
// *******************************************************************************

// Ensure phone starts with '+' so libphonenumber-js can auto-detect country.
// WhatsApp numbers always include the calling code but without '+' prefix.
function ensurePlus(phone) {
  if (!phone) return phone;
  const str = String(phone).trim();
  return str.startsWith('+') ? str : `+${str}`;
}

// Format a source phone number for display.
// Try with '+' prefix first; fall back to BR hint.
function formatSourcePhone(phone) {
  if (!phone) return null;
  const str = String(phone).trim();
  // Already has '+' prefix - auto-detect country
  if (str.startsWith('+')) {
    return parsePhoneToDisplay(str, undefined, { showCountryCode: true });
  }
  // No '+' prefix - prepend and let libphonenumber auto-detect
  const formatted = parsePhoneToDisplay(`+${str}`, undefined, { showCountryCode: true });
  if (formatted && formatted !== `+${str}`) return formatted;
  return `+${str}`;
}
